// PaieDashboard.jsx – Version corrigée
import React, { useState, useEffect, useCallback } from 'react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  PieChart, Pie, Cell, ResponsiveContainer
} from 'recharts';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faFileInvoiceDollar, faCoins, faHandHoldingUsd, faChartPie,
  faSpinner, faExclamationTriangle, faSyncAlt, faCheckCircle,
  faWallet
} from '@fortawesome/free-solid-svg-icons';
import API_CONFIG2 from '../../API_CONFIG/API_CONFIG2';

const API_URL = `${API_CONFIG2.apiUrl}employe/api_paie.php`;

const COLORS = {
  'Brouillon': '#fbbf24',
  'Validé': '#3b82f6',
  'Payé': '#22c55e',
  'Demandé': '#fbbf24',
  'Approuvé': '#3b82f6',
  'Remboursé': '#22c55e',
  'Annulé': '#ef4444'
};

const STATUS_ORDER_FICHE = ['Brouillon', 'Validé', 'Payé'];
const STATUS_ORDER_AVANCE = ['Demandé', 'Approuvé', 'Remboursé', 'Annulé'];

const PaieDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_URL}?action=get_dashboard_stats&_=${Date.now()}`);
      if (!res.ok) throw new Error('Erreur réseau');
      const json = await res.json();
      if (json.status === 'success') {
        setData(json.data);
      } else {
        throw new Error(json.message || 'Erreur chargement');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <div className="dashboard-loading">
        <FontAwesomeIcon icon={faSpinner} spin size="3x" />
        <p>Chargement du tableau de bord...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-error">
        <FontAwesomeIcon icon={faExclamationTriangle} /> Erreur : {error}
        <button onClick={loadData}><FontAwesomeIcon icon={faSyncAlt} /> Réessayer</button>
      </div>
    );
  }

  if (!data) return null;

  const { 
    total_fiches, 
    total_salaire_brut, 
    total_salaire_net, 
    total_cotisations, 
    total_avances,
    par_statut,
    evolution_mois,
    avances_stats
  } = data;

  // Données pour le camembert
  const statutFiches = STATUS_ORDER_FICHE.map(key => ({
    name: key,
    value: par_statut[key] || 0
  }));

  // Données pour les avances
  const avancesCountData = STATUS_ORDER_AVANCE.map(key => ({
    name: key,
    value: avances_stats[key]?.count || 0
  }));

  const avancesMontantData = STATUS_ORDER_AVANCE.map(key => ({
    name: key,
    value: avances_stats[key]?.total_montant || 0
  }));

  // Évolution mensuelle
  const evolution = evolution_mois.map(item => ({
    ...item,
    periode: item.periode.replace('-', '/')
  }));

  const totalFiches = total_fiches || 1;
  const payesCount = par_statut['Payé'] || 0;
  const progressionPayes = Math.round((payesCount / totalFiches) * 100);

  return (
    <div className="paie-dashboard">
        <style jsx>{`
        .paie-dashboard {
          padding: 1.5rem;
          background: rgba(10, 14, 39, 0.85);
          backdrop-filter: blur(12px);
          border-radius: 24px;
          border: 1px solid rgba(255,255,255,0.06);
          color: #e0e7ff;
          margin-top: 2rem;
        }
        .dashboard-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 2rem;
          flex-wrap: wrap;
          gap: 1rem;
        }
        .dashboard-header h2 {
          margin: 0;
          display: flex;
          align-items: center;
          gap: 0.75rem;
          font-weight: 600;
        }
        .dashboard-header h2 svg {
          color: #3b82f6;
        }
        .refresh-btn {
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.08);
          color: #e0e7ff;
          padding: 0.4rem 1.2rem;
          border-radius: 30px;
          cursor: pointer;
          transition: 0.2s;
          display: inline-flex;
          align-items: center;
          gap: 0.5rem;
          font-size: 0.85rem;
        }
        .refresh-btn:hover {
          background: rgba(255,255,255,0.1);
        }
        .kpi-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 1.5rem;
          margin-bottom: 2rem;
        }
        .kpi-card {
          background: rgba(255,255,255,0.03);
          border-radius: 16px;
          padding: 1rem 1.2rem;
          border: 1px solid rgba(255,255,255,0.04);
          transition: 0.2s;
        }
        .kpi-card:hover {
          background: rgba(255,255,255,0.05);
          transform: translateY(-2px);
        }
        .kpi-label {
          font-size: 0.8rem;
          text-transform: uppercase;
          color: #94a3b8;
          letter-spacing: 0.05em;
          display: flex;
          align-items: center;
          gap: 0.4rem;
        }
        .kpi-value {
          font-size: 1.8rem;
          font-weight: 700;
          margin-top: 0.3rem;
          color: #f8fafc;
        }
        .kpi-sub {
          font-size: 0.8rem;
          color: #94a3b8;
          margin-top: 0.2rem;
        }
        .progress-section {
          background: rgba(255,255,255,0.02);
          border-radius: 16px;
          padding: 1.2rem 1.5rem;
          margin-bottom: 2rem;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .progress-title {
          font-weight: 600;
          margin-bottom: 0.8rem;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .progress-bar-container {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-wrap: wrap;
        }
        .progress-bar-track {
          flex: 1;
          height: 24px;
          background: rgba(255,255,255,0.05);
          border-radius: 40px;
          overflow: hidden;
          display: flex;
          min-width: 150px;
        }
        .progress-segment {
          height: 100%;
          transition: width 0.6s ease;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 0.65rem;
          font-weight: 600;
          color: rgba(0,0,0,0.7);
        }
        .progress-legend {
          display: flex;
          gap: 1rem;
          flex-wrap: wrap;
          font-size: 0.8rem;
        }
        .legend-item {
          display: flex;
          align-items: center;
          gap: 0.3rem;
        }
        .legend-dot {
          width: 12px;
          height: 12px;
          border-radius: 4px;
        }
        .progress-percent {
          font-weight: 600;
          font-size: 1.1rem;
          white-space: nowrap;
        }
        .charts-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 2rem;
          margin-top: 1.5rem;
        }
        .chart-card {
          background: rgba(255,255,255,0.02);
          border-radius: 16px;
          padding: 1rem;
          border: 1px solid rgba(255,255,255,0.04);
        }
        .chart-title {
          font-size: 0.9rem;
          font-weight: 500;
          margin-bottom: 0.5rem;
          text-align: center;
          color: #cbd5e1;
        }
        .chart-container {
          height: 250px;
        }
        .dashboard-loading, .dashboard-error {
          text-align: center;
          padding: 3rem;
          background: rgba(10,14,39,0.7);
          border-radius: 24px;
          margin-top: 2rem;
        }
        .dashboard-error button {
          margin-top: 1rem;
          background: rgba(255,255,255,0.05);
          border: 1px solid rgba(255,255,255,0.1);
          color: #e0e7ff;
          padding: 0.5rem 1.5rem;
          border-radius: 30px;
          cursor: pointer;
        }
        @media (max-width: 768px) {
          .charts-grid {
            grid-template-columns: 1fr;
          }
          .kpi-grid {
            grid-template-columns: 1fr 1fr;
          }
          .progress-bar-track {
            height: 20px;
          }
        }
        @media (max-width: 480px) {
          .kpi-grid {
            grid-template-columns: 1fr;
          }
        }
      `}</style>

      <div className="dashboard-header">
        <h2><FontAwesomeIcon icon={faChartPie} /> Tableau de bord – Paie</h2>
        <button className="refresh-btn" onClick={loadData}>
          <FontAwesomeIcon icon={faSyncAlt} /> Rafraîchir
        </button>
      </div>

      {/* KPI Cards */}
      <div className="kpi-grid">
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faFileInvoiceDollar} /> Total fiches</div>
          <div className="kpi-value">{total_fiches}</div>
          <div className="kpi-sub">Dont {payesCount} payées</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faCoins} /> Masse salariale brute</div>
          <div className="kpi-value">{total_salaire_brut.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} USD</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faWallet} /> Net total</div>
          <div className="kpi-value">{total_salaire_net.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} USD</div>
        </div>
        <div className="kpi-card">
          <div className="kpi-label"><FontAwesomeIcon icon={faHandHoldingUsd} /> Avances totales</div>
          <div className="kpi-value">{total_avances.toLocaleString('fr-FR', { maximumFractionDigits: 0 })} USD</div>
        </div>
      </div>

      {/* Barre de progression */}
      <div className="progress-section">
        <div className="progress-title">
          <FontAwesomeIcon icon={faCheckCircle} /> Progression des fiches
          <span className="progress-percent">{progressionPayes}% payées</span>
        </div>
        <div className="progress-bar-container">
          <div className="progress-bar-track">
            {STATUS_ORDER_FICHE.map(status => {
              const count = par_statut[status] || 0;
              const percent = totalFiches > 0 ? (count / totalFiches) * 100 : 0;
              return (
                <div
                  key={status}
                  className="progress-segment"
                  style={{ width: `${percent}%`, background: COLORS[status] }}
                >
                  {percent > 6 && count}
                </div>
              );
            })}
          </div>
          <div className="progress-legend">
            {STATUS_ORDER_FICHE.map(status => (
              <span key={status} className="legend-item">
                <span className="legend-dot" style={{ background: COLORS[status] }}></span>
                {status} ({par_statut[status] || 0})
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Graphiques */}
      <div className="charts-grid">
        {/* Évolution mensuelle */}
        <div className="chart-card">
          <div className="chart-title">Évolution mensuelle (12 derniers mois)</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={evolution} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="periode" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#e0e7ff' }}
                  labelStyle={{ color: '#94a3b8' }}
                />
                <Legend wrapperStyle={{ fontSize: '0.7rem' }} />
                <Bar dataKey="total_brut" name="Brut" fill="#3b82f6" radius={[4,4,0,0]} />
                <Bar dataKey="total_net" name="Net" fill="#22c55e" radius={[4,4,0,0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Répartition des statuts (camembert) */}
        <div className="chart-card">
          <div className="chart-title">Répartition par statut</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={statutFiches}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={70}
                  innerRadius={40}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  labelLine={false}
                >
                  {statutFiches.map(entry => (
                    <Cell key={entry.name} fill={COLORS[entry.name] || '#94a3b8'} />
                  ))}
                </Pie>
                <Tooltip 
                  contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#e0e7ff' }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avances - Nombre */}
        <div className="chart-card">
          <div className="chart-title">Avances – Nombre par statut</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={avancesCountData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#e0e7ff' }} />
                <Bar dataKey="value" name="Nombre" fill="#8b5cf6" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Avances - Montants */}
        <div className="chart-card">
          <div className="chart-title">Avances – Montants par statut</div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={avancesMontantData} layout="vertical" margin={{ top: 5, right: 20, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis type="number" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <YAxis type="category" dataKey="name" stroke="#94a3b8" tick={{ fontSize: 10 }} />
                <Tooltip 
                  contentStyle={{ background: '#1e293b', border: 'none', borderRadius: 8, color: '#e0e7ff' }}
                  formatter={(value) => value.toLocaleString('fr-FR') + ' USD'}
                />
                <Bar dataKey="value" name="Montant" fill="#f59e0b" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaieDashboard;